// Hook para gestión de datos y estado de la propiedad

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getProperty,
  getPropertyBids,
  getPropertyInvites,
  verifyInvite,
  getBidConditions,
  subscribeToBids,
  subscribeToBlindOffers,
  subscribeToProperty,
  unsubscribe,
  getBuyerProfile,
} from '../services/supabaseService';
import { getUserBlindOffer } from '../services/supabase/blindOffers';
import { getPropertyStatus } from '../utils/propertyHelpers';

/**
 * Hook personalizado para gestionar el estado completo de una propiedad
 * Carga propiedad, invitación, pujas, perfil del comprador y se suscribe a cambios en tiempo real
 * @param {string} propertyId - UUID de la propiedad
 * @param {string} token - Token único de invitación del comprador
 * @returns {Object} Estado y funciones
 * @returns {Object|null} returns.property - Datos de la propiedad
 * @returns {Object|null} returns.invite - Invitación verificada del usuario
 * @returns {Array} returns.propertyBids - Ofertas de la propiedad (fase OPEN/LAST_CHANCE)
 * @returns {Array} returns.propertyInvites - Lista de invitados
 * @returns {Array} returns.availableConditions - Condiciones disponibles para ofertas
 * @returns {Object|null} returns.buyerProfile - Perfil completado del comprador
 * @returns {Object|null} returns.userBlindOffer - Oferta ciega del usuario (fase BLIND)
 * @returns {boolean} returns.loading - Si está cargando datos iniciales
 * @returns {string|null} returns.error - Mensaje de error si lo hay
 * @returns {Function} returns.refreshBids - Función para recargar ofertas
 * @returns {Function} returns.refreshProperty - Función para recargar propiedad
 * @returns {Function} returns.refreshBuyerProfile - Función para recargar perfil
 */
export const usePropertyState = (propertyId, token) => {
  const [property, setProperty] = useState(null);
  const [invite, setInvite] = useState(null);
  const [propertyBids, setPropertyBids] = useState([]);
  const [propertyInvites, setPropertyInvites] = useState([]);
  const [availableConditions, setAvailableConditions] = useState([]);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [userBlindOffer, setUserBlindOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs para acceder a valores actuales en callbacks de subscripciones
  const propertyRef = useRef(null);
  const inviteRef = useRef(null);

  /**
   * Cargar condiciones disponibles para las ofertas
   */
  const loadConditions = async () => {
    try {
      const conditions = await getBidConditions();
      setAvailableConditions(conditions);
    } catch (error) {
      console.error('Error cargando condiciones:', error);
    }
  };

  /**
   * Refrescar perfil del comprador
   */
  const refreshBuyerProfile = async () => {
    try {
      const profile = await getBuyerProfile(propertyId, token);
      setBuyerProfile(profile);
    } catch (error) {
      console.error('Error refrescando perfil:', error);
    }
  };

  /**
   * Validar acceso del usuario según estado de la propiedad
   * Retorna true si tiene acceso, false si debe ser bloqueado
   */
  const validateAccess = (propertyData, inviteData) => {
    if (!inviteData.hasAccess) {
      console.log('⛔ Acceso denegado: has_access=false');
      setError('INVALID_INVITE');
      return false;
    }
    return true;
  };

    /**
   * Cargar datos iniciales de la propiedad
   */
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const inviteData = await verifyInvite(propertyId, token);
      
      if (!inviteData) {
        setError('INVALID_INVITE');
        return;
      }

      setInvite(inviteData);
      inviteRef.current = inviteData; // Actualizar ref

      const [propertyData, bidsData, invitesData, profileData, blindOfferData] = await Promise.all([
        getProperty(propertyId),
        getPropertyBids(propertyId),
        getPropertyInvites(propertyId),
        getBuyerProfile(propertyId, token).catch(() => null),
        getUserBlindOffer(propertyId, inviteData.email).catch(() => null),
      ]);

      setProperty(propertyData);
      propertyRef.current = propertyData; // Actualizar ref
      setPropertyBids(bidsData);
      setPropertyInvites(invitesData);
      setBuyerProfile(profileData);
      setUserBlindOffer(blindOfferData);

      // Validar acceso según estado de la propiedad
      if (!validateAccess(propertyData, inviteData)) {
        return;
      }
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('LOAD_ERROR');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refrescar solo las ofertas
   */
  const refreshBids = useCallback(async () => {
    try {
      const bidsData = await getPropertyBids(propertyId);
      setPropertyBids(bidsData);
    } catch (err) {
      console.error('Error refrescando ofertas:', err);
    }
  }, [propertyId]);

  /**
   * Refrescar información de la invitación
   */
  const refreshInvite = async () => {
    try {
      const inviteData = await verifyInvite(propertyId, token);
      if (inviteData) {
        setInvite(inviteData);
      inviteRef.current = inviteData; // Actualizar ref
      }
    } catch (err) {
      console.error('Error refrescando invite:', err);
    }
  };

  /**
   * Refrescar datos de la propiedad
   */
  const refreshProperty = async () => {
    try {
      const propertyData = await getProperty(propertyId);
      if (propertyData) {
        // Validar acceso antes de actualizar
        if (invite && !validateAccess(propertyData, invite)) {
          console.log('⛔ refreshProperty: Acceso denegado tras actualización');
          return; // No actualizar si pierde acceso
        }
        setProperty(propertyData);
        propertyRef.current = propertyData; // Actualizar ref
      }
    } catch (err) {
      console.error('Error refrescando propiedad:', err);
    }
  };

  // Cargar datos iniciales al montar
  useEffect(() => {
    loadInitialData();
  }, [propertyId, token]);

  // Cargar condiciones al montar
  useEffect(() => {
    loadConditions();
  }, []);

  // Polling para refrescar bids cada 45 segundos
  useEffect(() => {
    if (!propertyId) return;
    
    const interval = setInterval(() => {
      refreshBids();
    }, 45000);

    return () => clearInterval(interval);
  }, [propertyId, refreshBids]);

  // Suscripción en tiempo real a nuevas ofertas
  useEffect(() => {
    if (!propertyId) return;

    const subscription = subscribeToBids(propertyId, (newBid) => {
      setPropertyBids(prevBids => {
        if (prevBids.some(b => b.id === newBid.id)) return prevBids;
        return [newBid, ...prevBids].sort((a, b) => b.amount - a.amount || new Date(a.timestamp) - new Date(b.timestamp));
      });
    });

    return () => {
      unsubscribe(subscription);
    };
  }, [propertyId]);

  // Suscripción en tiempo real a cambios en ofertas ciegas
  useEffect(() => {
    if (!propertyId || !invite?.email) return;

    const subscription = subscribeToBlindOffers(propertyId, (updatedOffer) => {
      // Solo actualizar si la oferta pertenece al usuario actual
      if (updatedOffer.buyerEmail === invite.email) {
        console.log('🔄 Oferta ciega actualizada en tiempo real:', updatedOffer);
        setUserBlindOffer(updatedOffer);
      }
    });

    return () => {
      unsubscribe(subscription);
    };
  }, [propertyId, invite?.email]);

  // Suscripción en tiempo real a cambios en la propiedad (anti-sniping, etc.)
  useEffect(() => {
    if (!propertyId) return;

    const subscription = subscribeToProperty(propertyId, (updatedProperty) => {
      console.log('🔄 Propiedad actualizada en tiempo real:', updatedProperty);
      
      // Usar refs para valores actuales
      const currentProperty = propertyRef.current;
      const currentInvite = inviteRef.current;
      
      // Crear objeto actualizado
      const updated = {
        ...currentProperty,
        ...updatedProperty,
      };
      
      // Validar acceso con los nuevos datos ANTES de actualizar
      if (currentInvite && !currentInvite.hasAccess) {
        console.log('⛔ Acceso denegado en tiempo real: has_access=false');
        setError('INVALID_INVITE');
        setProperty(null);
        propertyRef.current = null;
        return;
      }
      
      // Si pasa la validación, actualizar
      setProperty(updated);
      propertyRef.current = updated;
    });

    return () => {
      unsubscribe(subscription);
    };
  }, [propertyId]);

  // ✅ CRÍTICO: Validación continua de acceso
  // Se ejecuta cada vez que property o invite cambian
  useEffect(() => {
    if (!property || !invite) return;

    if (!invite.hasAccess) {
      console.log('⛔ Validación continua: Acceso denegado (has_access=false)');
      setError('INVALID_INVITE');
      setProperty(null);
      propertyRef.current = null;
    }
  }, [property, invite]);

  // Polling para refrescar UI cerca del deadline
  useEffect(() => {
    if (!property?.deadline) return;
    
    const deadline = new Date(property.deadline);
    const now = new Date();
    const timeUntilDeadline = deadline - now;
    
    // Si el deadline ya pasó, solo refrescar para actualizar UI
    if (timeUntilDeadline <= 0) {
      refreshProperty();
      return;
    }
    
    // Si faltan menos de 2 minutos, hacer polling cada 5 segundos
    if (timeUntilDeadline < 2 * 60 * 1000) {
      const interval = setInterval(() => {
        refreshProperty();
      }, 5000);
      
      return () => clearInterval(interval);
    }
    
    // Si el deadline está lejos, programar un timer para cuando falte 1 minuto
    const timerToStartPolling = setTimeout(() => {
      refreshProperty();
    }, timeUntilDeadline - 2 * 60 * 1000);
    
    return () => clearTimeout(timerToStartPolling);
  }, [property?.deadline, property?.last_chance_started_at]);

  return {
    // Estados
    property,
    invite,
    propertyBids,
    propertyInvites,
    availableConditions,
    buyerProfile,
    userBlindOffer,
    loading,
    error,
    // Funciones
    refreshBids,
    refreshInvite,
    refreshBuyerProfile,
    refreshProperty,
    setInvite,
  };
};
